import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import {
    Avatar,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Input,
    List,
    message,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import {
    CheckCircleOutlined,
    EditOutlined,
    FileTextOutlined,
    HistoryOutlined,
    MailOutlined,
    MedicineBoxOutlined,
    PhoneOutlined,
    PlusOutlined,
    SearchOutlined,
    UserAddOutlined,
    UserOutlined,
    UserSwitchOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { getAllPersonalRecords, getMedicalDoctorAttention, getMedicalOrderResultsByDni, getOrderTable, getPersonalRecordInformation, getPersonalRecordMedicalInformation, postMedicalOrderResult, postPersonalRecordPatient, deletePersonalRecord } from '../store/personalrecord/thunks';
import styles from './PatientManagement.module.scss';

const { Text, Title } = Typography;

type AnyRecord = Record<string, any>;

interface Patient {
    id?: string;
    dni: string;
    firstName: string;
    secondName?: string;
    lastName: string;
    secondLastname?: string;
    birthdate?: string;
    civilStatus?: string;
    gender?: string;
    levelStudy?: string;
    profession?: string;
    fingerPrintCode?: string;
    typeRelationship?: string;
    status?: string;
    phone?: AnyRecord;
    address?: AnyRecord;
    email?: string;
    demographicData?: AnyRecord;
    addresses?: AnyRecord[];
    emailPersonalRecords?: AnyRecord[];
}

interface PatientFormValues {
    dni: string;
    birthdate: Dayjs;
    firstName: string;
    secondName?: string;
    lastName: string;
    secondLastname?: string;
    gender: string;
    civilStatus?: string;
    typeRelationship: string;
    holderDni?: string;
    nationality?: string;
    levelStudy?: string;
    profession?: string;
    fingerPrintCode?: string;
    mobilePhone?: string;
    conventionalPhone?: string;
    emergencyPhone?: string;
    emergencyName?: string;
    email?: string;
    address?: string;
}

interface OrderItem {
    id?: string;
    orderCode?: number | string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    msIdentityCenterName?: string;
    msIdentityServiceName?: string;
    observation?: string;
    doctorId?: string;
    orderAttentionId?: string;
    attentionDate?: string;
    attentionType?: string;
    specialty?: string;
    doctorName?: string;
}

interface AttentionDocument {
    type?: string;
    url?: string;
    path?: string;
}

interface MedicalResult {
    id?: string;
    category?: string;
    reportName?: string;
    reportUrl?: string;
    timeStatus?: string;
    resultDate?: string;
    observation?: string;
    dicom?: string;
}

const valueOf = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key];
    }
    return undefined;
};

const responseArray = (source: any): AnyRecord[] => {
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.personalRecords)) return source.personalRecords;
    if (Array.isArray(source?.PersonalRecords)) return source.PersonalRecords;
    if (Array.isArray(source?.items)) return source.items;
    if (Array.isArray(source?.data)) return source.data;
    return [];
};

const normalizePatient = (sourceInput: AnyRecord): Patient => {
    // The information endpoint returns { count, personalRecord }, while the list endpoint returns { personalRecords }.
    const source = valueOf(sourceInput, 'personalRecord', 'PersonalRecord') || sourceInput;
    const rawPhone = valueOf(source, 'phone', 'Phone');
    const phone = (Array.isArray(rawPhone) ? rawPhone[0] : rawPhone) || {};
    const rawAddresses = valueOf(source, 'addresses', 'Addresses', 'address', 'Address');
    const addresses = Array.isArray(rawAddresses) ? rawAddresses : rawAddresses ? [rawAddresses] : [];
    const rawEmails = valueOf(source, 'emailPersonalRecords', 'EmailPersonalRecords', 'email', 'Email');
    const emails = Array.isArray(rawEmails) ? rawEmails : rawEmails ? [rawEmails] : [];
    const demographicData = valueOf(source, 'demographicData', 'DemographicData') || {};
    const directEmail = valueOf(source, 'email', 'Email');
    const email = typeof directEmail === 'string'
        ? directEmail
        : valueOf(emails[0], 'description', 'Description');

    return {
        id: valueOf(source, 'id', 'Id'),
        dni: String(valueOf(source, 'dni', 'Dni') || ''),
        firstName: String(valueOf(source, 'firstName', 'FirstName', 'firstname') || ''),
        secondName: valueOf(source, 'secondName', 'SecondName', 'secondname'),
        lastName: String(valueOf(source, 'lastName', 'LastName', 'lastname') || ''),
        secondLastname: valueOf(source, 'secondLastname', 'SecondLastname', 'secondLastname'),
        birthdate: valueOf(source, 'birthdate', 'Birthdate'),
        civilStatus: valueOf(source, 'civilStatus', 'CivilStatus') || valueOf(demographicData, 'civilStatus', 'CivilStatus'),
        gender: valueOf(source, 'gender', 'Gender'),
        levelStudy: valueOf(source, 'levelStudy', 'LevelStudy'),
        profession: valueOf(source, 'profession', 'Profession') || valueOf(demographicData, 'occupation', 'Occupation'),
        fingerPrintCode: valueOf(source, 'fingerPrintCode', 'FingerPrintCode'),
        typeRelationship: valueOf(source, 'typeRelationship', 'TypeRelationship'),
        status: valueOf(source, 'status', 'Status'),
        phone,
        address: addresses[0],
        email,
        demographicData,
        addresses,
        emailPersonalRecords: emails,
    };
};

const fullName = (patient: Patient) => [patient.firstName, patient.secondName, patient.lastName, patient.secondLastname]
    .filter(Boolean)
    .join(' ')
    .trim();

const initials = (patient: Patient) => [patient.firstName, patient.lastName]
    .filter(Boolean)
    .map((value) => value.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2) || 'PA';

const formatDate = (value?: string) => {
    if (!value) return 'No registrada';
    const date = dayjs(value);
    return date.isValid() ? date.format('DD/MM/YYYY') : 'No registrada';
};

const getAge = (value?: string) => {
    if (!value) return null;
    const date = dayjs(value);
    if (!date.isValid()) return null;
    return dayjs().diff(date, 'year');
};

const normalizeOrders = (source: any): OrderItem[] => {
    const values = responseArray(source);
    return values.map((item) => ({
        id: valueOf(item, 'id', 'Id', 'orderAttentionId', 'OrderAttentionId'),
        orderCode: valueOf(item, 'orderCode', 'OrderCode', 'orderAttentionCode', 'OrderAttentionCode'),
        status: valueOf(item, 'status', 'Status', 'timeStatus', 'TimeStatus'),
        createdAt: valueOf(item, 'createdAt', 'CreatedAt', 'orderAttentionCreatedAt', 'OrderAttentionCreatedAt'),
        updatedAt: valueOf(item, 'updatedAt', 'UpdatedAt'),
        msIdentityCenterName: valueOf(item, 'msIdentityCenterName', 'MsIdentityCenterName', 'centerName', 'CenterName'),
        msIdentityServiceName: valueOf(item, 'msIdentityServiceName', 'MsIdentityServiceName', 'serviceName', 'ServiceName'),
        observation: valueOf(item, 'observation', 'Observation'),
        doctorId: valueOf(item, 'doctorId', 'DoctorId') || valueOf(valueOf(item, 'doctor', 'Doctor'), 'id', 'Id'),
        orderAttentionId: valueOf(item, 'orderAttentionId', 'OrderAttentionId'),
        attentionDate: valueOf(item, 'attentionDate', 'AttentionDate', 'appointmentDate', 'AppointmentDate', 'orderAttentionCreatedAt', 'OrderAttentionCreatedAt'),
        attentionType: valueOf(item, 'type', 'Type', 'typeVisit', 'TypeVisit'),
        specialty: valueOf(item, 'specialty', 'Specialty', 'msIdentitySpecialityName', 'MsIdentitySpecialityName'),
        doctorName: valueOf(item, 'doctorName', 'DoctorName'),
    }));
};

const normalizeMedicalResults = (source: any): MedicalResult[] => responseArray(source)
    .map((item) => ({
        id: valueOf(item, 'id', 'Id'),
        category: String(valueOf(item, 'category', 'Category') || '').toUpperCase(),
        reportName: valueOf(item, 'reportName', 'ReportName'),
        reportUrl: valueOf(item, 'dicom', 'Dicom') || valueOf(item, 'reportName', 'ReportName'),
        timeStatus: valueOf(item, 'timeStatus', 'TimeStatus'),
        resultDate: valueOf(item, 'resultDate', 'ResultDate'),
        observation: valueOf(item, 'observation', 'Observation'),
        dicom: valueOf(item, 'dicom', 'Dicom'),
    }))
    .filter((item) => item.id || item.reportName);

const normalizeAttentionDocuments = (source: any): AttentionDocument[] => {
    const values = valueOf(source, 'files', 'Files', 'documents', 'Documents');
    if (!Array.isArray(values)) return [];
    return values.map((item) => ({
        type: valueOf(item, 'type', 'Type'),
        url: valueOf(item, 'url', 'Url'),
        path: valueOf(item, 'path', 'Path'),
    }));
};

const readFileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const value = String(reader.result || '');
        resolve(value.includes(',') ? value.split(',')[1] : value);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
});

const normalizeMedical = (source: AnyRecord | null): AnyRecord => source || {};

const getMedicalCount = (medical: AnyRecord, key: string) => {
    const value = valueOf(medical, key, key.charAt(0).toUpperCase() + key.slice(1));
    return Array.isArray(value) ? value.length : 0;
};

const patientToForm = (patient: Patient): Partial<PatientFormValues> => ({
    dni: patient.dni,
    birthdate: patient.birthdate ? dayjs(patient.birthdate) : undefined,
    firstName: patient.firstName,
    secondName: patient.secondName,
    lastName: patient.lastName,
    secondLastname: patient.secondLastname,
    gender: patient.gender,
    civilStatus: patient.civilStatus,
    typeRelationship: patient.typeRelationship || 'TITULAR',
    nationality: valueOf(patient.demographicData, 'nationality', 'Nationality'),
    levelStudy: patient.levelStudy,
    profession: patient.profession,
    fingerPrintCode: patient.fingerPrintCode,
    mobilePhone: valueOf(patient.phone, 'mobilePhone', 'MobilePhone'),
    conventionalPhone: valueOf(patient.phone, 'conventionalPhone', 'ConventionalPhone'),
    emergencyPhone: valueOf(patient.phone, 'emergencyPhone', 'EmergencyPhone'),
    emergencyName: valueOf(patient.phone, 'emergencyName', 'EmergencyName'),
    email: patient.email,
    address: valueOf(patient.address, 'description', 'Description'),
});

const toPatientPayload = (values: PatientFormValues, organizationId?: string, organizationName?: string) => ({
    dni: values.dni.trim(),
    holderDni: values.typeRelationship === 'TITULAR' ? null : values.holderDni?.trim() || null,
    firstName: values.firstName.trim(),
    secondName: values.secondName?.trim() || null,
    lastName: values.lastName.trim(),
    secondLastname: values.secondLastname?.trim() || null,
    birthdate: values.birthdate.format('YYYY-MM-DDT00:00:00'),
    civilStatus: values.civilStatus || null,
    levelStudy: values.levelStudy || null,
    profession: values.profession?.trim() || null,
    gender: values.gender,
    fingerPrintCode: values.fingerPrintCode?.trim() || null,
    typeRelationship: values.typeRelationship,
    address: values.address?.trim()
        ? { description: values.address.trim(), longitude: 0, latitude: 0 }
        : null,
    phone: values.mobilePhone || values.conventionalPhone || values.emergencyPhone
        ? {
            mobilePhone: values.mobilePhone?.trim() || null,
            conventionalPhone: values.conventionalPhone?.trim() || null,
            emergencyPhone: values.emergencyPhone?.trim() || null,
            emergencyName: values.emergencyName?.trim() || null,
        }
        : null,
    email: values.email?.trim()
        ? { description: values.email.trim(), type: 'PERSONAL' }
        : null,
    demographicData: values.nationality || values.profession || values.civilStatus
        ? {
            nationality: values.nationality?.trim() || 'Ecuatoriana',
            occupation: values.profession?.trim() || null,
            civilStatus: values.civilStatus || null,
            defaultLanguage: 'es',
        }
        : null,
    organizationId: organizationId || null,
    organizationName: organizationName || null,
});

const emptyForm: Partial<PatientFormValues> = {
    birthdate: dayjs().subtract(18, 'year'),
    gender: 'MASCULINO',
    typeRelationship: 'TITULAR',
    nationality: 'Ecuatoriana',
    civilStatus: 'SOLTERO',
};

export const PatientForm: React.FC<{
    editingPatient: Patient | null;
    onClose: () => void;
    onSaved: (dni: string) => void | Promise<void>;
    organizationId?: string;
    organizationName?: string;
    initialDni?: string;
    submitLabel?: string;
}> = ({ editingPatient, onClose, onSaved, organizationId, organizationName, initialDni, submitLabel }) => {
    const dispatch = useDispatch<any>();
    const [form] = Form.useForm<PatientFormValues>();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        form.setFieldsValue(editingPatient ? patientToForm(editingPatient) : { ...emptyForm, dni: initialDni });
    }, [editingPatient, form, initialDni]);

    const submit = async (values: PatientFormValues) => {
        setSaving(true);
        const result = await dispatch(postPersonalRecordPatient(toPatientPayload(values, organizationId, organizationName)) as any);
        setSaving(false);
        if (result !== undefined) {
            await onSaved(values.dni.trim());
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={submit} className={styles.form}>
            <div className={styles.formGrid}>
                <Card className={styles.formCard} title={<span><UserOutlined /> Identificación</span>}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="dni" label="Cédula / documento" rules={[{ required: true, message: 'Ingrese el documento' }]}>
                                <Input placeholder="0912345678" disabled={Boolean(editingPatient)} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="birthdate" label="Fecha de nacimiento" rules={[{ required: true, message: 'Seleccione la fecha' }]}>
                                <DatePicker className={styles.fullWidth} format="DD/MM/YYYY" placeholder="dd/mm/aaaa" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="firstName" label="Nombres" rules={[{ required: true, message: 'Ingrese los nombres' }]}>
                                <Input placeholder="Juan" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="secondName" label="Segundo nombre"><Input placeholder="Carlos" /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="lastName" label="Apellidos" rules={[{ required: true, message: 'Ingrese los apellidos' }]}>
                                <Input placeholder="Pérez" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="secondLastname" label="Segundo apellido"><Input placeholder="López" /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="gender" label="Sexo" rules={[{ required: true, message: 'Seleccione el sexo' }]}>
                                <Select options={[{ value: 'MASCULINO', label: 'Masculino' }, { value: 'FEMENINO', label: 'Femenino' }, { value: 'OTRO', label: 'Otro' }]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="civilStatus" label="Estado civil">
                                <Select options={['SOLTERO', 'CASADO', 'DIVORCIADO', 'VIUDO', 'UNION LIBRE'].map((value) => ({ value, label: value }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className={styles.formCard} title={<span><FileTextOutlined /> Datos complementarios</span>}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="typeRelationship" label="Relación" rules={[{ required: true, message: 'Seleccione la relación' }]}>
                                <Select options={[{ value: 'TITULAR', label: 'Titular' }, { value: 'DEPENDIENTE', label: 'Dependiente' }]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(previous, current) => previous.typeRelationship !== current.typeRelationship}>
                                {({ getFieldValue }) => getFieldValue('typeRelationship') === 'DEPENDIENTE' ? (
                                    <Form.Item name="holderDni" label="Cédula del titular" rules={[{ required: true, message: 'Ingrese la cédula del titular' }]}>
                                        <Input placeholder="0912345678" />
                                    </Form.Item>
                                ) : <Form.Item label="Cédula del titular"><Input value="El paciente es titular" disabled /></Form.Item>}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="nationality" label="Nacionalidad"><Input placeholder="Ecuatoriana" /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="levelStudy" label="Nivel de estudio"><Select allowClear options={['Primaria', 'Secundaria', 'Universitario', 'Postgrado'].map((value) => ({ value }))} /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="profession" label="Profesión"><Input placeholder="Ingeniero/a" /></Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="fingerPrintCode" label="Código huella"><Input placeholder="FPC12345" maxLength={11} /></Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className={styles.formCard} title={<span><PhoneOutlined /> Contacto</span>}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}><Form.Item name="mobilePhone" label="Teléfono móvil"><Input placeholder="0999999999" /></Form.Item></Col>
                        <Col xs={24} md={12}><Form.Item name="conventionalPhone" label="Teléfono convencional"><Input placeholder="022345678" /></Form.Item></Col>
                        <Col xs={24}><Form.Item name="email" label="Correo electrónico"><Input type="email" prefix={<MailOutlined />} placeholder="correo@ejemplo.com" /></Form.Item></Col>
                        <Col xs={24}><Form.Item name="address" label="Dirección"><Input placeholder="Av. Amazonas y Naciones Unidas" /></Form.Item></Col>
                    </Row>
                </Card>

                <Card className={styles.formCard} title={<span><UserSwitchOutlined /> Contacto de emergencia</span>}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}><Form.Item name="emergencyPhone" label="Teléfono de emergencia"><Input placeholder="0987654321" /></Form.Item></Col>
                        <Col xs={24} md={12}><Form.Item name="emergencyName" label="Nombre de emergencia"><Input placeholder="María Pérez" /></Form.Item></Col>
                    </Row>
                    <div className={styles.lockedNote}><MedicineBoxOutlined /><div><Text strong>Antecedentes, alergias y ficha clínica</Text><Text type="secondary">Se administran desde la pestaña Historia clínica cuando existan datos disponibles.</Text></div></div>
                </Card>
            </div>
            <div className={styles.formFooter}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="primary" htmlType="submit" loading={saving} icon={editingPatient ? <CheckCircleOutlined /> : <UserAddOutlined />}>
                    {submitLabel || (editingPatient ? 'Actualizar paciente' : 'Guardar paciente')}
                </Button>
            </div>
        </Form>
    );
};

const PatientSummary: React.FC<{ patient: Patient; onEdit: () => void; onDeactivate: () => void; organizationId?: string }> = ({ patient, onEdit, onDeactivate, organizationId }) => {
    const [activeTab, setActiveTab] = useState('summary');
    const age = getAge(patient.birthdate);
    const phone = patient.phone || {};
    return (
        <div className={styles.summary}>
            <div className={styles.profileHeading}>
                <div className={styles.profileIdentity}>
                    <Avatar size={64} className={styles.profileAvatar}>{initials(patient)}</Avatar>
                    <div><Title level={2}>{fullName(patient) || 'Paciente sin nombre'}</Title><Text type="secondary">{age !== null ? `${age} años` : 'Edad no registrada'} · C.I. {patient.dni}</Text></div>
                </div>
                <Space wrap>
                    <Button icon={<EditOutlined />} onClick={onEdit}>Actualizar paciente</Button>
                    <Button danger onClick={onDeactivate}>Inactivar</Button>
                </Space>
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab} destroyInactiveTabPane items={[
                {
                    key: 'summary', label: 'Resumen', children: <>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}><Card title={<span><PhoneOutlined /> Contacto</span>} className={styles.detailCard}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Teléfono">{valueOf(phone, 'mobilePhone', 'MobilePhone') || 'No registrado'}</Descriptions.Item>
                                    <Descriptions.Item label="Correo">{patient.email || 'No registrado'}</Descriptions.Item>
                                    <Descriptions.Item label="Dirección">{valueOf(patient.address, 'description', 'Description') || 'No registrada'}</Descriptions.Item>
                                </Descriptions>
                            </Card></Col>
                            <Col xs={24} lg={12}><Card title={<span><FileTextOutlined /> Información</span>} className={styles.detailCard}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Fecha de nacimiento">{formatDate(patient.birthdate)}</Descriptions.Item>
                                    <Descriptions.Item label="Sexo">{patient.gender || 'No registrado'}</Descriptions.Item>
                                    <Descriptions.Item label="Estado civil">{patient.civilStatus || 'No registrado'}</Descriptions.Item>
                                    <Descriptions.Item label="Estado"><Tag color={patient.status === 'ACTIVO' ? 'success' : 'default'}>{patient.status || 'SIN ESTADO'}</Tag></Descriptions.Item>
                                </Descriptions>
                            </Card></Col>
                        </Row>
                    </>,
                },
                {
                    key: 'personal', label: 'Datos personales', children: <Card className={styles.detailCard}>
                        <Descriptions bordered column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                            <Descriptions.Item label="Documento">{patient.dni}</Descriptions.Item>
                            <Descriptions.Item label="Relación">{patient.typeRelationship || 'No registrada'}</Descriptions.Item>
                            <Descriptions.Item label="Nacionalidad">{valueOf(patient.demographicData, 'nationality', 'Nationality') || 'No registrada'}</Descriptions.Item>
                            <Descriptions.Item label="Nivel de estudio">{patient.levelStudy || 'No registrado'}</Descriptions.Item>
                            <Descriptions.Item label="Profesión">{patient.profession || 'No registrada'}</Descriptions.Item>
                            <Descriptions.Item label="Código huella">{patient.fingerPrintCode || 'No registrado'}</Descriptions.Item>
                        </Descriptions>
                    </Card>,
                },
                { key: 'orders', label: <span><FileTextOutlined /> Pedidos y resultados</span>, children: <OrdersTab patient={patient} /> },
                { key: 'history', label: <span><HistoryOutlined /> Historia clínica</span>, children: <MedicalHistoryTab patient={patient} organizationId={organizationId} /> },
            ]} />
        </div>
    );
};

const OrdersTab: React.FC<{ patient: Patient }> = ({ patient }) => {
    const dispatch = useDispatch<any>();
    const [orders, setOrders] = useState<OrderItem[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [results, setResults] = useState<MedicalResult[]>([]);
    const [resultsLoading, setResultsLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('results');
    const [activeCategory, setActiveCategory] = useState('LABORATORIO');
    const [resultsRefresh, setResultsRefresh] = useState(0);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadCategory, setUploadCategory] = useState('LABORATORIO');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [selectedAttention, setSelectedAttention] = useState<OrderItem | null>(null);
    const [attentionDetail, setAttentionDetail] = useState<AnyRecord | null>(null);
    const [attentionLoading, setAttentionLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setOrdersLoading(true);
            setSelectedAttention(null);
            setAttentionDetail(null);
            const response = await dispatch(getOrderTable(patient.dni, dayjs().subtract(1, 'year').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')) as any);
            if (mounted) {
                const nextOrders = normalizeOrders(response);
                setOrders(nextOrders);
                setSelectedAttention(nextOrders[0] || null);
                setOrdersLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [dispatch, patient.dni]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!selectedAttention?.doctorId) {
                setAttentionDetail(null);
                setAttentionLoading(false);
                return;
            }

            setAttentionLoading(true);
            const response = await dispatch(getMedicalDoctorAttention(selectedAttention.doctorId) as any);
            if (mounted) {
                setAttentionDetail(response && typeof response === 'object' ? response : null);
                setAttentionLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [dispatch, selectedAttention?.doctorId]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setResultsLoading(true);
            const response = await dispatch(getMedicalOrderResultsByDni(patient.dni) as any);
            if (mounted) {
                setResults(normalizeMedicalResults(response));
                setResultsLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [dispatch, patient.dni, resultsRefresh]);

    const categories = [
        { key: 'LABORATORIO', label: 'Laboratorio' },
        { key: 'IMAGEN', label: 'Imagen' },
        { key: 'PROCEDIMIENTOS', label: 'Procedimientos' },
    ];

    const closeUpload = () => {
        setUploadOpen(false);
        setUploadFile(null);
        setUploadDescription('');
    };

    const openUpload = () => {
        setUploadCategory(activeCategory);
        setUploadFile(null);
        setUploadDescription('');
        setUploadOpen(true);
    };

    const saveUpload = async () => {
        if (!uploadFile) {
            message.warning('Seleccione un archivo para cargar el resultado.');
            return;
        }

        setUploading(true);
        try {
            const base64 = await readFileAsBase64(uploadFile);
            const response = await dispatch(postMedicalOrderResult({
                orderAttentionId: null,
                category: uploadCategory,
                fileName: uploadFile.name,
                base64,
                observation: uploadDescription.trim() || null,
                dicom: null,
                personalRecordDni: patient.dni,
                msIdentityCenterIdPerform: null,
                msIdentityCenterNamePerform: null,
            }) as any);

            if (response !== undefined) {
                message.success('Resultado guardado correctamente.');
                closeUpload();
                setResultsRefresh((value) => value + 1);
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'No se pudo cargar el resultado.');
        } finally {
            setUploading(false);
        }
    };

    const renderResults = (category: string) => {
        if (resultsLoading) return <div className={styles.tabLoading}><Spin /></div>;
        const categoryResults = results.filter((result) => result.category === category);
        if (!categoryResults.length) return <Empty description="Sin resultados cargados en esta categoría." />;

        return <List className={styles.orderList} dataSource={categoryResults} renderItem={(result) => {
            const title = result.reportName && !/^https?:\/\//i.test(result.reportName) ? result.reportName : 'Resultado médico';
            return <List.Item actions={result.reportUrl ? [<Button key="view" type="link" href={result.reportUrl} target="_blank" rel="noreferrer">Ver resultado</Button>] : undefined}>
                <List.Item.Meta
                    avatar={<Avatar icon={<FileTextOutlined />} />}
                    title={title}
                    description={<Space wrap size={[8, 4]}>
                        <Tag color={result.timeStatus === 'ANULADO' ? 'error' : 'success'}>{result.timeStatus || 'SIN ESTADO'}</Tag>
                        <Text type="secondary">{formatDate(result.resultDate)}</Text>
                    </Space>}
                />
                {result.observation && <Text type="secondary">{result.observation}</Text>}
            </List.Item>;
        }} />;
    };

    const renderAttentions = () => {
        if (ordersLoading) return <div className={styles.tabLoading}><Spin /></div>;
        if (!orders.length) return <Empty description="El paciente todavía no tiene atenciones registradas" />;

        const documents = normalizeAttentionDocuments(attentionDetail);
        const selectedStatus = selectedAttention?.status || 'SIN ESTADO';

        return <div className={styles.attentionLayout}>
            <List
                className={styles.attentionList}
                dataSource={orders}
                renderItem={(order) => {
                    const selected = selectedAttention?.doctorId === order.doctorId
                        && selectedAttention?.id === order.id;
                    const status = order.status || 'SIN ESTADO';
                    return <List.Item
                        className={`${styles.attentionItem} ${selected ? styles.attentionItemSelected : ''}`}
                        onClick={() => setSelectedAttention(order)}
                    >
                        <List.Item.Meta
                            avatar={<Avatar icon={<MedicineBoxOutlined />} />}
                            title={`Atención del: ${formatDate(order.attentionDate || order.createdAt)}`}
                            description={<Space wrap size={[6, 4]}>
                                <Tag color={status === 'ATENDIDO' ? 'success' : status === 'CONSULTA' ? 'processing' : 'default'}>{status}</Tag>
                                {order.msIdentityCenterName && <Tag>{order.msIdentityCenterName}</Tag>}
                                {(order.specialty || order.msIdentityServiceName) && <Tag>{order.specialty || order.msIdentityServiceName}</Tag>}
                            </Space>}
                        />
                    </List.Item>;
                }}
            />
            <div className={styles.attentionDetail}>
                {attentionLoading ? <div className={styles.tabLoading}><Spin /></div> : selectedAttention?.doctorId ? <Card
                    title="Documentos generados"
                    extra={<Tag color={selectedStatus === 'ATENDIDO' ? 'success' : 'default'}>{selectedStatus}</Tag>}
                >
                    <Text type="secondary">
                        Atención del: {formatDate(selectedAttention.attentionDate || selectedAttention.createdAt)}
                    </Text>
                    {documents.length ? <List
                        className={styles.documentList}
                        dataSource={documents}
                        renderItem={(document, index) => {
                            const href = document.url || document.path;
                            return <List.Item actions={href ? [<Button key="open" type="link" href={href} target="_blank" rel="noreferrer">Abrir</Button>] : undefined}>
                                <List.Item.Meta
                                    avatar={<Avatar icon={<FileTextOutlined />} />}
                                    title={document.type || `Documento ${index + 1}`}
                                />
                            </List.Item>;
                        }}
                    /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay documentos generados para esta atención" />}
                </Card> : <Empty description="Seleccione una atención para consultar sus documentos" />}
            </div>
        </div>;
    };

    return <>
        <Tabs
        activeKey={activeSection}
        onChange={setActiveSection}
        items={[
            {
                key: 'results',
                label: <span><FileTextOutlined /> Resultados <Tag>{results.length}</Tag></span>,
                children: <>
                    <div className={styles.resultsToolbar}>
                        <Text type="secondary">Resultados médicos registrados para {fullName(patient)}</Text>
                        <Space>
                            <Button size="small" onClick={() => setResultsRefresh((value) => value + 1)}>Actualizar</Button>
                            <Button type="primary" size="small" icon={<UploadOutlined />} onClick={openUpload}>Cargar resultado</Button>
                        </Space>
                    </div>
                    <Tabs
                        activeKey={activeCategory}
                        onChange={setActiveCategory}
                        items={categories.map((category) => ({
                            key: category.key,
                            label: <span>{category.label} <Tag>{results.filter((result) => result.category === category.key).length}</Tag></span>,
                            children: renderResults(category.key),
                        }))}
                    />
                </>,
            },
            { key: 'attentions', label: <span><MedicineBoxOutlined /> Atenciones</span>, children: renderAttentions() },
        ]}
        />
    <Modal
        open={uploadOpen}
        title="Cargar resultado"
        okText="Guardar resultado"
        cancelText="Cancelar"
        confirmLoading={uploading}
        onOk={saveUpload}
        onCancel={closeUpload}
        destroyOnClose
    >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <Tag color="blue">{categories.find((category) => category.key === uploadCategory)?.label}</Tag>
            <div>
                <Text strong>Seleccionar archivo</Text>
                <Input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                />
                <Text type="secondary">Formatos permitidos: PDF, PNG o JPG.</Text>
            </div>
            <div>
                <Text strong>Nombre / descripción del resultado</Text>
                <Input.TextArea
                    rows={3}
                    value={uploadDescription}
                    onChange={(event) => setUploadDescription(event.target.value)}
                    placeholder="Descripción del resultado"
                />
            </div>
        </Space>
        </Modal>
    </>;
};

const MedicalHistoryTab: React.FC<{ patient: Patient; organizationId?: string }> = ({ patient, organizationId }) => {
    const dispatch = useDispatch<any>();
    const [medical, setMedical] = useState<AnyRecord>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            const response = await dispatch(getPersonalRecordMedicalInformation(patient.dni, organizationId) as any);
            if (mounted) {
                setMedical(normalizeMedical(response));
                setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [dispatch, patient.dni, organizationId]);

    if (loading) return <div className={styles.tabLoading}><Spin /></div>;
    const cards = [
        { key: 'medicalHistory', label: 'Antecedentes', icon: <HistoryOutlined /> },
        { key: 'medications', label: 'Medicamentos', icon: <MedicineBoxOutlined /> },
        { key: 'allergies', label: 'Alergias', icon: <MedicineBoxOutlined /> },
        { key: 'vaccination', label: 'Vacunas', icon: <CheckCircleOutlined /> },
        { key: 'personalHabits', label: 'Hábitos', icon: <UserOutlined /> },
    ];
    return <>
        <Card className={styles.detailCard} title="Resumen de historia clínica">
            <Row gutter={[16, 16]}>{cards.map((card) => <Col xs={12} md={8} lg={4} key={card.key}><Statistic title={<span>{card.icon} {card.label}</span>} value={getMedicalCount(medical, card.key)} /></Col>)}</Row>
        </Card>
        <div className={styles.historyNote}><MedicineBoxOutlined /><div><Text strong>Ficha clínica</Text><Text type="secondary">Los registros clínicos se mantienen en el microservicio de Personal Record y se consultan desde aquí. Esta vista no crea endpoints nuevos.</Text></div></div>
    </>;
};

const PatientManagement: React.FC<{ organizationId?: string; organizationName?: string }> = ({ organizationId, organizationName }) => {
    const dispatch = useDispatch<any>();
    const [patients, setPatients] = useState<Patient[]>([]);
    const [selected, setSelected] = useState<Patient | null>(null);
    const [search, setSearch] = useState('');
    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

    const loadPatients = async (preferredDni?: string) => {
        setListLoading(true);
        const response = await dispatch(getAllPersonalRecords(organizationId) as any);
        const nextPatients = responseArray(response).map(normalizePatient).filter((patient) => patient.dni);
        setPatients(nextPatients);
        const next = preferredDni ? nextPatients.find((patient) => patient.dni === preferredDni) : nextPatients[0];
        if (next) await selectPatient(next, nextPatients);
        else setSelected(null);
        setListLoading(false);
    };

    const selectPatient = async (patient: Patient, sourcePatients = patients) => {
        setSelected(patient);
        setDetailLoading(true);
        const response = await dispatch(getPersonalRecordInformation(patient.dni, organizationId) as any);
        const detailed = response && typeof response === 'object' && !Array.isArray(response) ? normalizePatient(response) : null;
        if (detailed?.dni) {
            setSelected(detailed);
            setPatients(sourcePatients.map((item) => item.dni === detailed.dni ? { ...item, ...detailed } : item));
        }
        setDetailLoading(false);
    };

    // The loader is intentionally kept out of the dependency list because it also updates the selected patient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadPatients(); }, [organizationId]);

    const filteredPatients = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return patients;
        return patients.filter((patient) => `${fullName(patient)} ${patient.dni}`.toLowerCase().includes(query));
    }, [patients, search]);

    const openCreate = () => { setEditingPatient(null); setDrawerOpen(true); };
    const openEdit = () => { if (selected) { setEditingPatient(selected); setDrawerOpen(true); } };
    const closeDrawer = () => { setDrawerOpen(false); setEditingPatient(null); };
    const saved = async (dni: string) => { closeDrawer(); await loadPatients(dni); };

    const deactivate = () => {
        if (!selected) return;
        Modal.confirm({
            title: 'Inactivar paciente',
            content: `¿Desea inactivar a ${fullName(selected)}? Sus datos históricos se conservarán.`,
            okText: 'Inactivar',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: async () => { await dispatch(deletePersonalRecord(selected.dni) as any); await loadPatients(); },
        });
    };

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div><Title level={2}>Pacientes</Title><Text type="secondary">Administra la información personal y clínica con las APIs existentes.</Text></div>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Nuevo paciente</Button>
            </div>
            <div className={styles.workspace}>
                <aside className={styles.patientRail}>
                    <Input allowClear prefix={<SearchOutlined />} placeholder="Buscar por nombre o cédula..." value={search} onChange={(event) => setSearch(event.target.value)} />
                    <div className={styles.railMeta}><Text type="secondary">{filteredPatients.length} pacientes</Text><Button type="text" size="small" onClick={() => loadPatients()}>Actualizar</Button></div>
                    {listLoading ? <div className={styles.railLoading}><Spin /></div> : filteredPatients.length ? <List className={styles.patientList} dataSource={filteredPatients} renderItem={(patient) => (
                        <List.Item className={`${styles.patientItem} ${selected?.dni === patient.dni ? styles.patientItemSelected : ''}`} onClick={() => selectPatient(patient)}>
                            <List.Item.Meta avatar={<Avatar className={styles.listAvatar}>{initials(patient)}</Avatar>} title={fullName(patient) || 'Paciente sin nombre'} description={<span>{getAge(patient.birthdate) !== null ? `${getAge(patient.birthdate)} años` : 'Edad no registrada'} · {patient.dni}</span>} />
                            <Tag color={patient.status === 'ACTIVO' ? 'success' : 'default'}>{patient.status || 'SIN ESTADO'}</Tag>
                        </List.Item>
                    )} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay pacientes" />}
                </aside>
                <main className={styles.detailPanel}>
                    {detailLoading ? <div className={styles.detailLoading}><Spin size="large" /></div> : selected ? <PatientSummary patient={selected} organizationId={organizationId} onEdit={openEdit} onDeactivate={deactivate} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Seleccione un paciente para administrar su información" />}
                </main>
            </div>
            <Drawer open={drawerOpen} onClose={closeDrawer} width="100%" title={editingPatient ? `Editar paciente · ${fullName(editingPatient)}` : 'Registrar paciente'} destroyOnClose>
                <PatientForm editingPatient={editingPatient} organizationId={organizationId} organizationName={organizationName} onClose={closeDrawer} onSaved={saved} />
            </Drawer>
        </div>
    );
};

export default PatientManagement;
